import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Address from '@afosto/app-components-react/Address';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import Property from '@afosto/app-components-react/Property';
import { boolean as booleanFormatter } from '@afosto/app-components-react/DataGrid/formatters';
import ShippingMethodForm from '../ShippingMethodForm';
import getFullName from '@afosto/utils/getFullName';
import translations from './translations';
import { date } from '../DataGrid/formatters';
import dayjs from 'dayjs';

const ShippingMethodFormSection = ({
  clientId,
  shipping,
  setShipping,
  shippingAddress,
  showSplitOption,
  showDueAt,
}) => {
  const intl = useIntl();
  const [showShipmentDrawer, setShowShipmentDrawer] = useState(false);
  const { isSplitAllowed = false, method, servicePoint, deliveryAt } = shipping ?? {};

  const handleSubmit = shipment => {
    setShipping({
      ...(showSplitOption ? { isSplitAllowed: shipment?.isSplitAllowed ?? false } : {}),
      method: shipment?.method,
      servicePoint: shipment?.method?.is_carrier_location ? shipment?.servicePoint : null,
      deliveryAt: shipment?.deliveryAt
        ? dayjs(shipment?.deliveryAt).hour(12).minute(0).second(0).format()
        : null,
    });
    setShowShipmentDrawer(false);
  };

  return (
    <>
      <Card style={{ marginBottom: 8 }}>
        <CardHeader
          title={intl.formatMessage(translations.page.shipmentFormSection.title)}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            <Button color="primary" variant="outlined" onClick={() => setShowShipmentDrawer(true)}>
              {intl.formatMessage(translations.page.shipmentFormSection.button.edit.label)}
            </Button>
          }
        />
        <Box px={2} pt={1} pb={2}>
          <Box pb={1}>
            <Property
              label={intl.formatMessage(translations.fields.shippingMethod.label)}
              value={method?.name}
            />
          </Box>

          {servicePoint && (
            <Box pb={1}>
              <Property
                label={intl.formatMessage(translations.fields.servicePoint.label)}
                value={
                  <Address
                    company={servicePoint?.address?.organisation}
                    fullName={getFullName(servicePoint?.address)}
                    addressLine1={servicePoint?.address?.address_line_1}
                    addressLine2={servicePoint?.address?.address_line_2}
                    postalCode={servicePoint?.address?.postal_code}
                    city={servicePoint?.address?.locality}
                    country={servicePoint?.address?.country_code}
                    administrativeArea={servicePoint?.address?.administrative_area}
                  />
                }
              />
            </Box>
          )}

          {showSplitOption && (
            <Box pb={1}>
              <Property
                label={intl.formatMessage(translations.fields.isSplitAllowed.label)}
                value={booleanFormatter(isSplitAllowed)}
              />
            </Box>
          )}
          {showDueAt && (
            <Box pb={1}>
              <Property
                label={intl.formatMessage(translations.fields.deliveryAt.label)}
                value={date(deliveryAt)}
              />
            </Box>
          )}
        </Box>
      </Card>
      <FormDrawer
        title={intl.formatMessage(translations.page.shipmentFormSection.title)}
        isOpen={showShipmentDrawer}
        onClose={() => setShowShipmentDrawer(false)}
        drawerWidth="400px"
      >
        <ShippingMethodForm
          clientId={clientId}
          shipment={shipping}
          countryCode={shippingAddress?.country_code}
          postalCode={shippingAddress?.postal_code}
          onCancel={() => setShowShipmentDrawer(false)}
          onSubmit={handleSubmit}
          showSplitOption={showSplitOption}
          showDueAt={showDueAt}
        />
      </FormDrawer>
    </>
  );
};

ShippingMethodFormSection.propTypes = {
  clientId: PropTypes.string,
  setShipping: PropTypes.func,
  shipping: PropTypes.object,
  shippingAddress: PropTypes.object,
  showSplitOption: PropTypes.bool,
  showDueAt: PropTypes.bool,
};

ShippingMethodFormSection.defaultProps = {
  clientId: undefined,
  setShipping: undefined,
  shipping: {},
  shippingAddress: {},
  showSplitOption: true,
  showDueAt: false,
};

export default ShippingMethodFormSection;
